import React from 'react';
import {
  AlertTriangle,
  Home,
  RefreshCw,
  Route,
  Settings2,
  WifiOff,
  Wrench,
  WashingMachine
} from 'lucide-react';

const MODE_META = {
  error: {
    label: 'Что-то пошло не так',
    accent: 'from-blue-600 via-sky-500 to-cyan-400',
    chip: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/25 dark:text-red-300 dark:ring-red-900/40',
    icon: AlertTriangle,
    iconTone: 'text-red-500 dark:text-red-300'
  },
  offline: {
    label: 'Нет подключения',
    accent: 'from-blue-600 via-indigo-500 to-sky-400',
    chip: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-950/25 dark:text-red-300 dark:ring-red-900/40',
    icon: WifiOff,
    iconTone: 'text-red-500 dark:text-red-300'
  },
  notFound: {
    label: 'Раздел не найден',
    accent: 'from-blue-600 via-violet-500 to-sky-400',
    chip: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/25 dark:text-amber-300 dark:ring-amber-900/40',
    icon: Route,
    iconTone: 'text-amber-500 dark:text-amber-300'
  }
};

const DEFAULT_ACTION_ICONS = {
  reload: RefreshCw,
  home: Home,
  retry: RefreshCw,
  settings: Settings2
};

const ActionButton = ({ action }) => {
  const Icon = action.icon || DEFAULT_ACTION_ICONS[action.kind] || RefreshCw;
  const tone = action.tone || 'primary';
  const className = tone === 'secondary'
    ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
    : 'border-transparent bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600';

  if (action.href) {
    return (
      <a
        href={action.href}
        className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors ${className}`}
      >
        <Icon className="h-4 w-4" />
        <span>{action.label}</span>
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={action.onClick}
      className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors ${className}`}
    >
      <Icon className="h-4 w-4" />
      <span>{action.label}</span>
    </button>
  );
};

const AppStatusScreen = ({
  mode = 'error',
  title,
  description,
  details = '',
  actions = [],
  compact = false
}) => {
  const meta = MODE_META[mode] || MODE_META.error;
  const SecondaryIcon = meta.icon;

  return (
    <div className={`flex min-h-full items-center justify-center ${compact ? 'py-2' : 'py-6 sm:py-10'}`}>
      <div className="w-full max-w-xl">
        <div className="overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.08)] dark:border-gray-800 dark:bg-gray-900">
          <div className={`relative overflow-hidden bg-gradient-to-br ${meta.accent} px-6 pb-10 pt-8 text-white sm:px-8`}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.26),transparent_48%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.18),transparent_40%)]" />
            <div className="relative flex items-start justify-between gap-4">
              <span className="inline-flex items-center rounded-full bg-white/16 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90 ring-1 ring-white/20 backdrop-blur-sm">
                Большая Стирка
              </span>
              <span className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-white/14 ring-1 ring-white/20 backdrop-blur-sm ${meta.iconTone}`}>
                <SecondaryIcon className="h-5 w-5 text-white" />
              </span>
            </div>

            <div className="relative mt-7 flex items-center justify-center">
              <div className="relative flex h-36 w-36 items-center justify-center rounded-[32px] bg-white/14 ring-1 ring-white/18 backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]">
                <div className="absolute inset-3 rounded-[26px] border border-white/12 bg-white/8" />
                <WashingMachine className="relative z-[1] h-16 w-16 text-white" strokeWidth={1.9} />
                <div className="absolute -bottom-1 -right-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-lg ring-4 ring-white/18 dark:text-blue-400">
                  {mode === 'offline' ? <WifiOff className="h-5 w-5" /> : <Wrench className="h-5 w-5" />}
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 pt-5 sm:px-8 sm:pb-8">
            <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${meta.chip}`}>
              {meta.label}
            </div>

            <h2 className="mt-4 text-2xl font-semibold tracking-tight text-gray-950 dark:text-gray-50">
              {title}
            </h2>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {description}
            </p>

            {details ? (
              <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:border-gray-800 dark:bg-gray-950/40 dark:text-gray-300">
                {details}
              </div>
            ) : null}

            {actions.length > 0 ? (
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                {actions.map((action) => (
                  <div key={action.label} className="sm:min-w-[180px]">
                    <ActionButton action={action} />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppStatusScreen;
